/**
 * ACTIVITY TYPES — Proiezione leggera bulk delle attività di un'entità (TS-59).
 *
 * `ActivitySummary` rispecchia lo schema omonimo del backend
 * (rest-openapi.yaml → RelationshipsResourceImpl.getEntityActivities): un'unione
 * discriminata dal campo `type`, con i campi type-specific valorizzati solo per
 * il tipo pertinente. È la sola proiezione dell'attività per la riga CHIUSA della
 * timeline; il record completo (edit body dell'accordion aperto) si carica lazy
 * all'espansione via le getX singole. relationshipId/pinned e il "primo contatto"
 * NON sono qui: restano derivati client-side dalle Relationship già caricate.
 */

import type { MessageAttachment, MessageDirection } from '../message/messageTypes';

export type ActivitySummaryType =
  | 'TASK'
  | 'NOTE'
  | 'CALL'
  | 'EMAIL'
  | 'MEETING'
  | 'LINKEDIN'
  | 'MESSAGE';

export interface ActivitySummary {
  /** Discriminatore dell'unione. */
  type: ActivitySummaryType;
  /** FQN Java dell'attività (es. it.dvel.crm.core.model.Message). */
  className: string;
  /** Primary key dell'attività. */
  classPK: number;
  /** Testo della riga chiusa (titolo / oggetto / snippet descrizione). */
  title: string;
  /** Data per l'ordinamento desc (createDate/emailDate/.../receiptDate). */
  date: string;
  /** "Chi" mostrato nella riga (owner; per MESSAGE il mittente). */
  userName: string;
  /** Solo MESSAGE — direzione (IN/OUT). */
  direction?: MessageDirection;
  /**
   * Solo MESSAGE — metadati degli allegati della mail (TS-108). Stanno sulla proiezione bulk e
   * non su quella del corpo perche' servono anche a chi NON apre l'accordion: la card "Allegati"
   * della scheda preventivo li elenca all'apertura. Il contenuto si scarica a parte.
   */
  attachments?: MessageAttachment[];
  /** Solo TASK — stato come id numerico dell'enum TaskStatus (int32). */
  status?: number;
  /** Solo TASK — data di scadenza. */
  expirationDate?: string | null;
}

/** Stato dello slice `activities`: proiezioni bulk indicizzate per entità. */
export interface ActivityState {
  /** Chiave `${fqn}:${classPK}` → lista attività dell'entità (già ordinata desc). */
  byEntity: Record<string, ActivitySummary[]>;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

/** Costruisce la chiave dello slice per un'entità (FQN + id). */
export function entityActivitiesKey(className: string, classPK: number): string {
  return `${className}:${classPK}`;
}
